// Overflow / timeout / no-agent handling of a queue, as in the Teams admin
// center: what happens, after which limit, and where the call goes.

import type { ReactNode } from 'react';
import type { QueueException, ExceptionAction, Prompt } from '../../api/client';
import { inputCls, labelCls, clampInt } from './common';
import { PromptEditor } from './PromptEditor';

const ACTIONS: Array<{ value: ExceptionAction; label: string }> = [
  { value: 'none', label: 'Keep in queue / nothing' },
  { value: 'disconnect', label: 'Disconnect' },
  { value: 'forward', label: 'Redirect to…' },
  { value: 'voicemail', label: 'Voicemail (person)' },
  { value: 'sharedVoicemail', label: 'Shared voicemail (group)' },
];

const TITLES = { overflow: 'Overflow (queue full)', timeout: 'Timeout (maximum wait)', noAgent: 'No agents available' } as const;
const THRESHOLD = {
  overflow: { label: 'Maximum calls waiting', max: 200 },
  timeout: { label: 'Maximum wait (seconds)', max: 2700 },
  noAgent: null,
} as const;

export function QueueExceptionForm({ kind, value, onChange, targets, groups, audioPicker }: {
  kind: 'overflow' | 'timeout' | 'noAgent';
  value?: QueueException;
  onChange: (v: QueueException | undefined) => void;
  targets: Array<{ value: string; label: string }>;
  groups: Array<{ id: string; name?: string }>;
  audioPicker?: (value: Prompt, onChange: (v: Prompt) => void) => ReactNode;
}) {
  const ex: QueueException = value || { action: kind === 'noAgent' ? 'none' : 'disconnect' };
  const set = (patch: Partial<QueueException>) => onChange({ ...ex, ...patch });
  const threshold = THRESHOLD[kind];
  const needsTarget = ex.action === 'forward' || ex.action === 'voicemail';
  const isShared = ex.action === 'sharedVoicemail';
  return (
    <div className="border border-gray-200 rounded p-2.5 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-800">{TITLES[kind]}</span>
        {value && <button type="button" onClick={() => onChange(undefined)} className="text-[11px] text-gray-400 hover:text-red-600">clear</button>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={labelCls}>Action</label>
          <select value={ex.action} onChange={e => set({ action: e.target.value as ExceptionAction })} className={inputCls}>
            {ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </div>
        {threshold ? (
          <div>
            <label className={labelCls}>{threshold.label}</label>
            <input type="number" min={0} max={threshold.max} value={ex.threshold ?? ''} className={inputCls}
              onChange={e => set({ threshold: clampInt(e.target.value, threshold.max) })} />
          </div>
        ) : (
          <div>
            <label className={labelCls}>Applies to</label>
            <select value={ex.applyTo || ''} onChange={e => set({ applyTo: (e.target.value || undefined) as QueueException['applyTo'] })} className={inputCls}>
              <option value="">— not set —</option>
              <option value="allCalls">All calls</option>
              <option value="newCalls">New calls only</option>
            </select>
          </div>
        )}
        {needsTarget && (
          <div className="col-span-2">
            <label className={labelCls}>Routes to</label>
            <select value={ex.targetId || ''} onChange={e => set({ targetId: e.target.value || undefined })} className={inputCls}>
              <option value="">— none —</option>
              {targets.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
        )}
        {isShared && (
          <div className="col-span-2">
            <label className={labelCls}>Voicemail group (M365 group id)</label>
            <input list={`groups-${kind}`} type="text" value={ex.sharedVoicemailGroupId || ''} placeholder="00000000-0000-0000-0000-000000000000"
              onChange={e => set({ sharedVoicemailGroupId: e.target.value.trim().toLowerCase() || undefined })} className={inputCls} />
            <datalist id={`groups-${kind}`}>
              {groups.map(g => <option key={g.id} value={g.id}>{g.name || g.id}</option>)}
            </datalist>
            <div className="flex gap-4 mt-1.5">
              <label className="flex items-center gap-1.5 text-xs text-gray-700">
                <input type="checkbox" checked={!!ex.transcription} onChange={e => set({ transcription: e.target.checked })} className="accent-purple-600" />Transcription
              </label>
              <label className="flex items-center gap-1.5 text-xs text-gray-700">
                <input type="checkbox" checked={!!ex.suppressSystemPrompt} onChange={e => set({ suppressSystemPrompt: e.target.checked })} className="accent-purple-600" />Skip system prompt
              </label>
            </div>
          </div>
        )}
      </div>
      {ex.action !== 'none' && ex.action !== 'forward' && (
        <PromptEditor label="Message played" value={ex.prompt} onChange={p => set({ prompt: p })} audioPicker={audioPicker} />
      )}
    </div>
  );
}
